// Tick snapshot types for Market Depth / Tick Analysis

export interface DepthLevel {
  price: number;
  quantity: number;
  orders: number;
}

export interface OHLC {
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface Depth {
  buy: DepthLevel[];
  sell: DepthLevel[];
}

export interface TickData {
  mode: string;
  instrument_token: number;
  is_tradable: boolean;
  is_index: boolean;
  timestamp: string;
  last_trade_time: string;
  last_price: number;
  last_traded_quantity: number;
  total_buy_quantity: number;
  total_sell_quantity: number;
  volume_traded: number;
  total_buy: number;
  total_sell: number;
  average_trade_price: number;
  oi: number;
  oi_day_high: number;
  oi_day_low: number;
  net_change: number;
  ohlc: OHLC;
  depth: Depth;
}

export interface StockInfo {
  isin: string;
  stock_name: string;
  nse_code: string;
  bse_code: string;
  stock_code: string;
  industry_name: string;
  sector_name: string;
  trading_view_navlink: string;
  screener_navlink: string;
}

export interface FinancialInfo {
  current_price: number;
  price_to_earning: number | null;
  market_capitalization: number;
  promoter_holding: number;
  return_over_1day: number;
  return_over_1week: number;
  return_over_1month: number;
  return_over_3months: number;
  return_over_6months: number;
  return_over_1year: number;
  volume: number;
  volume_1week_average: number;
  volume_1month_average: number;
  return_on_equity: number;
  debt_to_equity: number;
  eps: number;
  price_to_book_value: number;
  down_from_52w_high: number;
  is_sme: boolean;
}

export interface ESMInfo {
  is_in_esm: boolean;
  current_stage: number | null;
  entry_date: string | null;
  last_exit_date: string | null;
  current_state_date: string | null;
  transaction_type: 'entry' | 'exit' | 'moved_up' | 'moved_down' | null;
}

export interface GSMInfo {
  is_in_gsm: boolean;
  current_stage: string | null;
  entry_date: string | null;
  last_exit_date: string | null;
  criteria_name: string | null;
}

export interface TickSnapshot {
  id: number;
  trading_symbol: string;
  exchange: string;
  instrument_token: number;
  last_price: number;
  volume_traded: number;
  tick_data: TickData;
  captured_at: string;
  created_at: string;
  updated_at: string;
  stock_info: StockInfo | null;
  financial_info: FinancialInfo | null;
  esm_info: ESMInfo | null;
  gsm_info: GSMInfo | null;
  is_matched: boolean;
}

export interface TickSnapshotApiResponse {
  status: string;
  count: number;
  matched_count: number;
  unmatched_count: number;
  data: TickSnapshot[];
}

// Filter types for screener-like filtering
export type FilterOperator = '>' | '<' | '>=' | '<=' | '=' | '!=';

export interface ScreenerFilter {
  id: string;
  field: string;
  operator: FilterOperator;
  value: number;
}

// Fields available for filtering (numeric fields from the API response)
export interface FilterableField {
  key: string;
  label: string;
  category: 'tick' | 'ohlc' | 'depth' | 'financial' | 'general' | 'esm_gsm';
}

export const FILTERABLE_FIELDS: FilterableField[] = [
  // General
  { key: 'last_price', label: 'Last Price', category: 'general' },
  { key: 'volume_traded', label: 'Volume Traded', category: 'general' },
  
  // Tick Data
  { key: 'tick_data.last_traded_quantity', label: 'Last Traded Qty', category: 'tick' },
  { key: 'tick_data.total_buy_quantity', label: 'Total Buy Qty', category: 'tick' },
  { key: 'tick_data.total_sell_quantity', label: 'Total Sell Qty', category: 'tick' },
  { key: 'tick_data.average_trade_price', label: 'Avg Trade Price', category: 'tick' },
  { key: 'tick_data.net_change', label: 'Net Change', category: 'tick' },
  { key: 'tick_data.oi', label: 'Open Interest', category: 'tick' },
  
  // OHLC
  { key: 'tick_data.ohlc.open', label: 'Open', category: 'ohlc' },
  { key: 'tick_data.ohlc.high', label: 'High', category: 'ohlc' },
  { key: 'tick_data.ohlc.low', label: 'Low', category: 'ohlc' },
  { key: 'tick_data.ohlc.close', label: 'Close', category: 'ohlc' },
  
  // Financial
  { key: 'financial_info.current_price', label: 'Fin. Current Price', category: 'financial' },
  { key: 'financial_info.price_to_earning', label: 'P/E Ratio', category: 'financial' },
  { key: 'financial_info.market_capitalization', label: 'Market Cap (Cr)', category: 'financial' },
  { key: 'financial_info.promoter_holding', label: 'Promoter Holding %', category: 'financial' },
  { key: 'financial_info.return_over_1day', label: '1D Return %', category: 'financial' },
  { key: 'financial_info.return_over_1week', label: '1W Return %', category: 'financial' },
  { key: 'financial_info.return_over_1month', label: '1M Return %', category: 'financial' },
  { key: 'financial_info.return_over_3months', label: '3M Return %', category: 'financial' },
  { key: 'financial_info.return_over_6months', label: '6M Return %', category: 'financial' },
  { key: 'financial_info.return_over_1year', label: '1Y Return %', category: 'financial' },
  { key: 'financial_info.volume', label: 'Volume', category: 'financial' },
  { key: 'financial_info.volume_1week_average', label: 'Avg Volume (1W)', category: 'financial' },
  { key: 'financial_info.volume_1month_average', label: 'Avg Volume (1M)', category: 'financial' },
  { key: 'financial_info.return_on_equity', label: 'ROE', category: 'financial' },
  { key: 'financial_info.debt_to_equity', label: 'D/E Ratio', category: 'financial' },
  { key: 'financial_info.eps', label: 'EPS', category: 'financial' },
  { key: 'financial_info.price_to_book_value', label: 'P/B Ratio', category: 'financial' },
  { key: 'financial_info.down_from_52w_high', label: 'Down from 52W High %', category: 'financial' },
  
  // Calculated / Derived
  { key: 'buy_sell_qty_ratio', label: 'Buy/Sell Qty Ratio', category: 'depth' },
  { key: 'bid_ask_spread', label: 'Bid-Ask Spread', category: 'depth' },
  { key: 'change_percent', label: 'Change %', category: 'general' },
  
  // ESM/GSM
  { key: 'esm_info.current_stage', label: 'ESM Stage', category: 'esm_gsm' },
  { key: 'gsm_info.current_stage', label: 'GSM Stage', category: 'esm_gsm' },
];

// ESM/GSM Filter options for dropdown filters
export type ESMFilterOption = 'all' | 'in_esm' | 'not_in_esm' | 'esm_1' | 'esm_2';
export type GSMFilterOption = 'all' | 'in_gsm' | 'not_in_gsm';

